/* import __COLOCATED_TEMPLATE__ from './call-listening-modal.hbs'; */
/* RESPONSIBLE TEAM: team-phone */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type AdminSummary from 'embercom/objects/inbox/admin-summary';
import type TwilioService from 'embercom/services/twilio-service';
import type IntlService from 'embercom/services/intl';
import type InboxState from 'embercom/services/inbox-state';
import { action } from '@ember/object';
import type PhoneNumber from 'embercom/models/inbox/phone-number';
import { tracked } from '@glimmer/tracking';
import type IntercomCallService from 'embercom/services/intercom-call-service';
import type AdminAwayService from 'embercom/services/admin-away-service';
import type Session from 'embercom/services/session';
import { PHONE_NUMBER_COUNTRY_DETAILS } from 'embercom/models/settings/calling';
import moment from 'moment-timezone';

interface Args {}

interface Signature {
  Args: Args;
}

export default class CallListeningModal extends Component<Signature> {
  @service declare twilioService: TwilioService;
  @service declare intercomCallService: IntercomCallService;
  @service declare adminAwayService: AdminAwayService;
  @service declare intl: IntlService;
  @service declare inboxState: InboxState;
  @service declare session: Session;

  @tracked workspacePhoneNumber?: PhoneNumber;
  @tracked adminOnCall?: AdminSummary;
  @tracked showBargeConfirmation = false;
  @tracked isBarging = false;

  constructor(owner: unknown, args: any) {
    super(owner, args);
    this.acceptCall();
  }

  get startTime() {
    return moment().valueOf();
  }

  get userSummary() {
    return this.twilioService.userSummary;
  }

  get customerLabelText() {
    return this.intl.t('calling.modal.listening.customer-label', {
      name: this.userSummary?.displayAs,
      number: this.userSummary?.phone,
    });
  }

  get workspacePhoneNumberText() {
    let phoneNumbersFlag = PHONE_NUMBER_COUNTRY_DETAILS.get(
      this.twilioService.calledNumberCountryCode as string,
    )?.flag;
    return this.intl.t('calling.modal.listening.workplace-phone-number', {
      flag: phoneNumbersFlag,
      name: this.twilioService.calledNumberCountryCode,
    });
  }

  get isCallStillActive() {
    return this.twilioService.isActiveCall;
  }

  @action async acceptCall() {
    await this.twilioService.acceptListeningCall(this.inboxState.activeConversationId);
    this.adminOnCall = this.twilioService?.conversation?.adminAssignee;
  }

  @action
  async stopListening() {
    if (this.intercomCallService.listeningCallId !== undefined) {
      await this.intercomCallService.stopListeningToCallInProgress(
        this.intercomCallService.listeningCallId,
      );
    }
  }

  @action
  goToConversation() {
    this.twilioService.navigateToConversation();
  }

  get isBargeEnabled() {
    return this.session.workspace?.isFeatureEnabled('phone-barge') ?? false;
  }

  @action
  promptBarge() {
    this.showBargeConfirmation = true;
  }

  @action
  cancelBarge() {
    this.showBargeConfirmation = false;
  }

  @action
  async confirmBarge() {
    this.showBargeConfirmation = false;
    this.isBarging = true;
    try {
      await this.intercomCallService.bargeIntoCall(this.intercomCallService.listeningCallId!);
      this.twilioService.transitionFromListeningToParticipant();
    } catch (e: unknown) {
      // Barge failure is surfaced to the user by re-enabling the button so they can retry
      this.isBarging = false;
    }
  }

  willDestroy() {
    super.willDestroy();

    // Reset the listening call ID to ensure clean state
    this.intercomCallService.listeningCallId = undefined;

    // Refresh admin away settings when the listening modal is closing
    this.adminAwayService.getAwaySettings();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::CallListeningModal': typeof CallListeningModal;
    'inbox2/call-listening-alert': typeof CallListeningModal;
  }
}
